import React, { useState, useEffect } from 'react';
import API from '../api'; 
import { useNavigate, useLocation } from 'react-router-dom';

const ScheduleAppointment = () => {
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [services, setServices] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [availableSlots, setAvailableSlots] = useState([]);
    const [patientQuery, setPatientQuery] = useState('');
    const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        patientId: '',
        doctorId: '',
        serviceId: '',
        startTime: '',
        note: ''
    });

    const formatPatient = (p) => `${p.firstName} ${p.lastName} — ${p.phoneNumber}`;

    const patientSuggestions = patientQuery.trim().length > 0
        ? patients.filter(p => {
            const q = patientQuery.toLowerCase();
            const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
            return fullName.includes(q) || (p.phoneNumber || '').includes(patientQuery);
        }).slice(0, 8)
        : [];

    const handlePatientQueryChange = (e) => {
        setPatientQuery(e.target.value);
        setShowPatientSuggestions(true);
        setFormData(prev => ({ ...prev, patientId: '' }));
    };

    const selectPatient = (patient) => {
        setFormData(prev => ({ ...prev, patientId: patient.id }));
        setPatientQuery(formatPatient(patient));
        setShowPatientSuggestions(false);
    };

    // 1. Učitavanje osnovnih podataka + Hvatanje parametara iz URL-a
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [resPatients, resDoctors, resServices] = await Promise.all([
                    API.get('/patients'),
                    API.get('/doctors'),
                    API.get('/medical-services')
                ]);
                
                setPatients(resPatients.data);
                setDoctors(resDoctors.data);
                setServices(resServices.data);

                // --- Provera URL parametara (iz Sveske) ---
                const params = new URLSearchParams(location.search);
                const dateParam = params.get('date'); // npr. 2026-05-04T10:15:00.000Z
                const doctorIdParam = params.get('doctorId'); // ako je Sveska filtrirana na jednog doktora

                if (dateParam) {
                    const fullDate = new Date(dateParam);
                    const dateOnly = fullDate.toISOString().split('T')[0];
                    const timeOnly = fullDate.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });

                    setSelectedDate(dateOnly);
                    setFormData(prev => ({
                        ...prev,
                        startTime: timeOnly,
                        doctorId: doctorIdParam || prev.doctorId
                    }));
                    // Slotovi zavise i od usluge (trajanje), koja se bira ručno posle ovoga,
                    // pa se tek tada zovu (vidi handleServiceChange).
                }

                if (location.state && location.state.selectedPatientId) {
                    setFormData(prev => ({ ...prev, patientId: location.state.selectedPatientId }));
                    const preselected = resPatients.data.find(p => p.id === location.state.selectedPatientId);
                    if (preselected) {
                        setPatientQuery(formatPatient(preselected));
                    }
                }
            } catch (err) {
                console.error("Greška pri dohvatanju podataka:", err);
                if (err.response?.status === 403) {
                    alert("Niste prijavljeni ili nemate pristup.");
                    navigate('/login');
                }
            }
        };
        fetchData();
    }, [navigate, location]);

    // 2. Dohvatanje slobodnih slotova - zavisi i od usluge, jer njeno trajanje
    // određuje da li se novi termin preklapa sa postojećim terminima tog doktora.
    const fetchSlots = async (doctorId, date, serviceId) => {
        if (doctorId && date && serviceId) {
            try {
                const res = await API.get(`/appointments/free-slots`, {
                    params: { doctorId, date, serviceId }
                });
                setAvailableSlots(res.data);
            } catch (err) {
                console.error("Greška pri dohvatanju slotova:", err);
            }
        } else {
            setAvailableSlots([]);
        }
    };

    // 3. Hendlovanje promena
    const handleDoctorChange = (e) => {
        const doctorId = e.target.value;
        // Vreme se NE briše ovde - ako je preuzeto iz Sveske (ili ranije izabrano),
        // ostaje predloženo (prikazuje se kao izabrano dugme) i posle izbora doktora.
        setFormData({ ...formData, doctorId });
        fetchSlots(doctorId, selectedDate, formData.serviceId);
    };

    const handleServiceChange = (e) => {
        const serviceId = e.target.value;
        setFormData({ ...formData, serviceId });
        fetchSlots(formData.doctorId, selectedDate, serviceId);
    };

    const handleDateChange = (e) => {
        const date = e.target.value;
        setSelectedDate(date);
        setFormData({ ...formData, startTime: '' });
        fetchSlots(formData.doctorId, date, formData.serviceId);
    };

    const handleSchedule = async () => {
        try {
            const requestBody = {
                patientId: formData.patientId,
                doctorId: formData.doctorId,
                serviceId: formData.serviceId,
                date: selectedDate,
                startTime: formData.startTime,
                note: formData.note
            };

            await API.post('/appointments', requestBody);
            alert("Uspešno zakazano!");
            navigate('/dashboard'); 
        } catch (err) {
            console.error("Greška pri zakazivanju:", err);
            alert(err.response?.data?.message || "Greška pri zakazivanju.");
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={{ textAlign: 'center', color: '#1e293b', marginBottom: '25px' }}>Novi Pregled</h2>
            
            <label style={styles.label}>Pacijent:</label>
            <div style={styles.autocompleteWrapper}>
                <input
                    type="text"
                    style={{
                        ...styles.input,
                        marginBottom: 0,
                        border: formData.patientId ? '1px solid #10b981' : styles.input.border
                    }}
                    placeholder="Ukucajte ime, prezime ili broj telefona..."
                    value={patientQuery}
                    onChange={handlePatientQueryChange}
                    onFocus={() => setShowPatientSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowPatientSuggestions(false), 150)}
                />
                {showPatientSuggestions && patientSuggestions.length > 0 && (
                    <div style={styles.suggestionsList}>
                        {patientSuggestions.map(p => (
                            <div
                                key={p.id}
                                style={styles.suggestionItem}
                                onMouseDown={() => selectPatient(p)}
                            >
                                {formatPatient(p)}
                            </div>
                        ))}
                    </div>
                )}
                {showPatientSuggestions && patientQuery.trim().length > 0 && patientSuggestions.length === 0 && (
                    <div style={styles.suggestionsList}>
                        <div style={{ ...styles.suggestionItem, cursor: 'default', color: '#94a3b8' }}>
                            Nema pacijenata za "{patientQuery}"
                        </div>
                    </div>
                )}
            </div>
            <div style={{ marginBottom: '20px' }} />

            <label style={styles.label}>Doktor:</label>
            <select 
                onChange={handleDoctorChange} 
                style={styles.input}
                value={formData.doctorId}
            >
                <option value="">Izaberi doktora...</option>
                {doctors.map(d => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
            </select>

            <label style={styles.label}>Usluga:</label>
            <select
                onChange={handleServiceChange}
                style={styles.input}
                value={formData.serviceId}
            >
                <option value="">Izaberi uslugu...</option>
                {services.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.durationMinutes} min)</option>
                ))}
            </select>

            <label style={styles.label}>Datum:</label>
            <input 
                type="date" 
                onChange={handleDateChange} 
                style={styles.input} 
                value={selectedDate}
            />

            {/* Prikaz slobodnih slotova */}
            <label style={styles.label}>Termin:</label>
            <div style={styles.slotGrid}>
                {/* Ako je termin došao iz URL-a, prikaži ga kao selektovanog čak i ako fetchSlots još nije gotov */}
                {formData.startTime && !availableSlots.includes(formData.startTime) && (
                    <button 
                        style={{...styles.slotBtn, backgroundColor: '#10b981', color: '#fff'}}
                    >
                        {formData.startTime}
                    </button>
                )}
                
                {availableSlots.map(slot => (
                    <button 
                        key={slot} 
                        onClick={() => setFormData({...formData, startTime: slot})}
                        style={{
                            ...styles.slotBtn, 
                            backgroundColor: formData.startTime === slot ? '#10b981' : '#f1f5f9',
                            color: formData.startTime === slot ? '#fff' : '#1e293b',
                            border: formData.startTime === slot ? '1px solid #059669' : '1px solid #cbd5e1'
                        }}
                    >
                        {slot}
                    </button>
                ))}
            </div>

            <label style={styles.label}>Napomena (opciono):</label>
            <textarea 
                style={{...styles.input, height: '80px', resize: 'none'}} 
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Dodatne informacije..."
            />

            <button 
                onClick={handleSchedule} 
                style={{
                    ...styles.mainBtn, 
                    opacity: (!formData.startTime || !formData.serviceId || !formData.patientId || !formData.doctorId) ? 0.6 : 1
                }} 
                disabled={!formData.startTime || !formData.serviceId || !formData.patientId || !formData.doctorId}
            >
                Potvrdi zakazivanje
            </button>
        </div>
    );
};

// ... stilovi ostaju isti kao tvoji ...
const styles = {
    container: { maxWidth: '500px', margin: '40px auto', padding: '30px', background: '#ffffff', borderRadius: '16px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' },
    label: { display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569', fontSize: '14px' },
    input: { width: '100%', padding: '12px', marginBottom: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '15px', outline: 'none', boxSizing: 'border-box' },
    autocompleteWrapper: { position: 'relative' },
    suggestionsList: {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        zIndex: 10,
        maxHeight: '220px',
        overflowY: 'auto'
    },
    suggestionItem: {
        padding: '10px 12px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#1e293b',
        borderBottom: '1px solid #f1f5f9'
    },
    slotGrid: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
        gap: '8px', 
        marginBottom: '20px' 
    },
    slotBtn: { padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s', border: '1px solid #cbd5e1' },
    mainBtn: { width: '100%', padding: '16px', background: '#1c8aa6', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }
};

export default ScheduleAppointment;